import { useState, type ReactElement } from 'react';
import { api, type ActiveShift, type StopView, type TaskView } from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { EMPTY, formatDateTime, formatTime } from '../shared/lib/format';
import { sameDay } from '../shared/lib/period';
import { useAsync } from '../shared/lib/use-async';
import {
  Card,
  EmptyState,
  primaryButtonStyle,
  secondaryButtonStyle,
} from '../shared/ui/primitives';

/**
 * Рейсы и задания (ТЗ разд. 8.3, 8.4, FR-540).
 *
 * Экран отвечает на два вопроса диспетчера: как поедет курьер и где рейс не
 * укладывается. Поэтому рядом с порядком объезда показаны план, факт и опоздание к
 * временному окну — расхождение видно до того, как курьер туда доедет (FR-616).
 *
 * Порядок точек здесь не правится: его меняет пересчёт или сам курьер (FR-802).
 * Точки, которые курьер переставил, помечены «Вручную» — пересчёт их не трогает.
 */

const REFRESH_MS = 20_000;

export function RunsPage(): ReactElement {
  const messages = useMessages();
  const [driverId, setDriverId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Не снапшот карты: он прячет смену без GPS-точек, а рейс от координат не зависит.
  const live = useAsync(() => api.activeShifts(), [], { intervalMs: REFRESH_MS });
  const drivers = live.data?.shifts ?? [];
  const selected = drivers.find((driver) => driver.driver_id === driverId) ?? drivers[0] ?? null;
  const shiftId = selected?.shift_id ?? null;

  const tasks = useAsync(async () => (shiftId === null ? [] : api.shiftTasks(shiftId)), [shiftId]);

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
        <h1 style={{ flex: 1 }}>{messages.cabinet.runs.title}</h1>

        {shiftId !== null && (
          <button
            type="button"
            style={secondaryButtonStyle}
            onClick={() => {
              void api.replanShift(shiftId).then(() => {
                setNotice(null);
                tasks.reload();
              });
            }}
          >
            {messages.cabinet.runs.replan}
          </button>
        )}
      </header>

      <DriverPicker
        drivers={drivers}
        selectedId={selected?.driver_id ?? null}
        onSelect={(id) => {
          setDriverId(id);
          setNotice(null);
        }}
        label={messages.cabinet.runs.pickDriver}
      />

      {notice !== null && <Card tone="ok">{notice}</Card>}

      {shiftId === null ? (
        <EmptyState text={messages.cabinet.runs.empty} />
      ) : tasks.loading && tasks.data === null ? (
        <EmptyState text={messages.cabinet.runs.loading} />
      ) : (tasks.data ?? []).length === 0 ? (
        <EmptyState text={messages.cabinet.runs.empty} />
      ) : (
        (tasks.data ?? []).map((task) => <RunCard key={task.id} task={task} />)
      )}
    </div>
  );
}

function DriverPicker({
  drivers,
  selectedId,
  onSelect,
  label,
}: {
  readonly drivers: readonly ActiveShift[];
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
  readonly label: string;
}): ReactElement {
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>

      {drivers.map((driver) => (
        <button
          key={driver.driver_id}
          type="button"
          onClick={() => onSelect(driver.driver_id)}
          style={driver.driver_id === selectedId ? primaryButtonStyle : secondaryButtonStyle}
        >
          {driver.driver_name ?? driver.driver_id}
        </button>
      ))}
    </div>
  );
}

/**
 * Один рейс.
 *
 * Время у точки — то же прогнозное прибытие, что и в ленте «Доставки»: план расчёта
 * устаревает к вечеру, и два разных времени у одного заказа на двух экранах читаются
 * как ошибка. Порядок меняет пересчёт или курьер; ручной перестановки здесь нет.
 */
function RunCard({ task }: { readonly task: TaskView }): ReactElement {
  const messages = useMessages();

  return (
    <Card tone={task.status === 'in_progress' ? 'progress' : 'neutral'}>
      <div
        style={{ display: 'flex', gap: 'var(--sp-4)', alignItems: 'baseline', flexWrap: 'wrap' }}
      >
        <h2 style={{ flex: 1 }}>
          {`${messages.cabinet.runs.run} ${task.run_number} · ${taskStatusLabel(messages, task.status)}`}
        </h2>

        <span className="num">
          {`${messages.cabinet.runs.distance}: ${formatKm(task.planned_distance_m, messages.cabinet.runs.km)}`}
        </span>
        <span className="num">
          {`${messages.cabinet.runs.returnAt}: ${formatTime(task.eta_return_at)}`}
        </span>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
            <th>{messages.cabinet.runs.sequence}</th>
            <th>{messages.cabinet.runs.type}</th>
            <th>{messages.cabinet.runs.order}</th>
            <th>{messages.cabinet.runs.address}</th>
            <th>{messages.cabinet.runs.arrival}</th>
            <th>{messages.cabinet.runs.fact}</th>
            <th>{messages.cabinet.runs.window}</th>
          </tr>
        </thead>

        <tbody>
          {task.stops.map((stop, index) => (
            <StopRow key={stop.id} stop={stop} position={index + 1} />
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function StopRow({
  stop,
  position,
}: {
  readonly stop: StopView;
  readonly position: number;
}): ReactElement {
  const messages = useMessages();
  const settled = ['completed', 'failed', 'cancelled'].includes(stop.status);
  const arrival = stop.eta_at ?? stop.planned_arrival_at;
  const late = settled ? null : lateMinutes(arrival, stop.delivery_window_to);

  return (
    <tr
      style={{
        borderTop: '1px solid var(--line)',
        opacity: settled ? 'var(--muted-opacity)' : 1,
        boxShadow: late === null ? 'none' : 'inset var(--rail-w) 0 0 var(--danger)',
      }}
    >
      <td className="num">{position}</td>
      <td>
        {stopTypeLabel(messages, stop.type)}
        {stop.is_pinned && ` · ${messages.cabinet.runs.pinned}`}
        {stop.is_locked && ` · ${messages.cabinet.runs.locked}`}
      </td>
      <td className="num">{stop.order_display_id ?? EMPTY}</td>
      <td>{stop.address ?? stop.location_name ?? EMPTY}</td>
      <td className="num" style={{ color: late === null ? undefined : 'var(--danger)' }}>
        {formatTime(arrival)}
      </td>
      <td className="num">
        {formatTime(stop.actual_arrival_at)}
        {stop.deviation_alert && (
          <span style={{ color: 'var(--danger)' }}>
            {` ${messages.cabinet.runs.deviation}: ${String(stop.eta_deviation_minutes)}′`}
          </span>
        )}
      </td>
      <td className="num">
        {windowLabel(messages, stop.delivery_window_from, stop.delivery_window_to)}
        {late !== null && (
          <span
            style={{
              display: 'block',
              color: 'var(--danger)',
              fontWeight: 'var(--fw-semibold)',
            }}
          >
            {lateLabel(messages, late)}
          </span>
        )}
      </td>
    </tr>
  );
}

const MINUTE_MS = 60_000;

/** Опоздание прогноза к концу окна в минутах; `null` — успевает или окна нет. */
export function lateMinutes(arrival: string | null, windowTo: string | null): number | null {
  if (arrival === null || windowTo === null) {
    return null;
  }

  const minutes = Math.round((Date.parse(arrival) - Date.parse(windowTo)) / MINUTE_MS);

  return Number.isNaN(minutes) || minutes <= 0 ? null : minutes;
}

const MINUTES_IN_HOUR = 60;

/** «опоздание 25 мин», больше часа — «опоздание 2 ч 5 мин»: 1323 минуты не читаются. */
export function lateLabel(messages: ReturnType<typeof useMessages>, minutes: number): string {
  const labels = messages.cabinet.runs;

  return minutes < MINUTES_IN_HOUR
    ? labels.lateBy.replace('{minutes}', String(minutes))
    : labels.lateByHours
        .replace('{hours}', String(Math.floor(minutes / MINUTES_IN_HOUR)))
        .replace('{minutes}', String(minutes % MINUTES_IN_HOUR));
}

/**
 * «с 10:00 до 22:40», «до 22:40», «с 10:00» — без тире с пустой половиной. Окно не
 * сегодняшнее — с датой: «до 01.11 22:40», иначе вчерашнее окно читается как сегодняшнее.
 */
export function windowLabel(
  messages: ReturnType<typeof useMessages>,
  from: string | null,
  to: string | null,
  now: Date = new Date(),
): string {
  const labels = messages.cabinet.runs;
  const show = (iso: string | null): string =>
    iso !== null && sameDay(new Date(iso), now) ? formatTime(iso) : formatDateTime(iso);

  if (from === null && to === null) {
    return EMPTY;
  }

  if (from === null) {
    return labels.windowTo.replace('{to}', show(to));
  }

  if (to === null) {
    return labels.windowFrom.replace('{from}', show(from));
  }

  return labels.windowRange.replace('{from}', show(from)).replace('{to}', show(to));
}

function taskStatusLabel(messages: ReturnType<typeof useMessages>, status: string): string {
  const labels = messages.cabinet.runs.statuses;

  return labels[status as keyof typeof labels] ?? status;
}

function stopTypeLabel(messages: ReturnType<typeof useMessages>, type: string): string {
  switch (type) {
    case 'pickup':
      return messages.stop.typePickup;
    case 'dropoff':
      return messages.stop.typeDropoff;
    case 'return':
      return messages.stop.typeReturn;
    default:
      return type;
  }
}

const METERS_IN_KM = 1000;

function formatKm(distanceM: number | null, unit: string): string {
  return distanceM === null ? EMPTY : `${(distanceM / METERS_IN_KM).toFixed(1)} ${unit}`;
}
